import React, { createContext, useContext, useEffect, useState } from 'react';
import { initDatabase } from '../db/indexeddb';
import { syncEngine } from '../sync/syncEngine';
import { onNetworkStatusChange, isOnline } from '../utils/network';
import { offlineQueue } from '../queue/offlineQueue';

const OfflineContext = createContext(undefined);

export const OfflineProvider = ({
  children,
  authToken,
  userToken,
  orgId,
}) => {
  const [online, setOnline] = useState(isOnline());
  const [isSyncing, setIsSyncing] = useState(false);
  const [queueSize, setQueueSize] = useState(0);

  // Initialize database
  useEffect(() => {
    initDatabase().catch(console.error);
  }, []);

  // Monitor network status
  useEffect(() => {
    const cleanup = onNetworkStatusChange(
      async () => {
        setOnline(true);
        console.log('🌐 Online - triggering sync');
        
        if (authToken && userToken && orgId) {
          setIsSyncing(true);
          try {
            await syncEngine.syncOnReconnect(authToken, userToken, orgId);
          } finally {
            setIsSyncing(false);
          }
        }
      },
      () => {
        setOnline(false);
        console.log('📴 Offline mode');
      }
    );

    return cleanup;
  }, [authToken, userToken, orgId]);

  // Initial sync on app load + Start auto-sync when authenticated
  useEffect(() => {
    if (authToken && userToken && orgId) {
      // Trigger initial sync immediately on first load
      console.log('🚀 Initial sync triggered - loading RFQ and all data...');
      syncEngine.syncAllInitialData(authToken, userToken, orgId).catch(err => {
        console.error('❌ Initial sync error:', err);
      });
      
      // Start auto-sync (every 30 seconds)
      syncEngine.startAutoSync(authToken, userToken, orgId, 30000);
      
      return () => {
        syncEngine.stopAutoSync();
      };
    }
  }, [authToken, userToken, orgId]);

  // Monitor queue size
  useEffect(() => {
    const updateQueueSize = async () => {
      const size = await offlineQueue.getSize();
      setQueueSize(size);
    };

    updateQueueSize();
    const interval = setInterval(updateQueueSize, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, []);

  const manualSync = async () => {
    if (!authToken || !userToken || !orgId) return;

    setIsSyncing(true);
    try {
      await syncEngine.manualSync(authToken, userToken, orgId);
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <OfflineContext.Provider
      value={{
        isOnline: online,
        isSyncing,
        queueSize,
        manualSync,
      }}
    >
      {children}
    </OfflineContext.Provider>
  );
};

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within OfflineProvider');
  }
  return context;
};
